<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Header & Actions -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Track system activities and user actions</p>
          </div>
          <div class="flex gap-3">
            <button 
              @click="showFilter = !showFilter"
              class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {{ showFilter ? 'Hide Filters' : 'Show Filters' }}
            </button>
            <button
              @click="exportLogs"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-[#74bd54] hover:bg-[#63a43f] transition-colors shadow-sm"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>

        <!-- Filters Panel -->
        <Transition name="filter">
          <div v-if="showFilter" class="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div class="lg:col-span-2">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Search</label>
                <input 
                  v-model="search" 
                  placeholder="Search user, action, or IP..." 
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" 
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Action Type</label>
                <select 
                  v-model="selectedAction" 
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition"
                >
                  <option value="">All Actions</option>
                  <option value="LOGIN_SUCCESS">Login Success</option>
                  <option value="LOGIN_FAILED">Login Failed</option>
                  <option value="VIEW_MANPOWER">View Manpower</option>
                  <!-- Add more actions as needed -->
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">From Date</label>
                <input 
                  v-model="fromDate" 
                  type="date" 
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" 
                />
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">To Date</label>
                <input 
                  v-model="toDate" 
                  type="date" 
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" 
                />
              </div>
            </div>
            <div class="mt-4 flex justify-end">
              <button 
                @click="resetFilters" 
                class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </Transition>

        <!-- Audit Logs Table -->
        <div v-if="!loading" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Entity</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">IP Address</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date & Time</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="log in filteredLogs" :key="log.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500 dark:text-gray-400">#{{ log.id }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">{{ log.user_full_name || 'System' }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">{{ log.user_type || 'Unknown' }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getActionBadgeClass(log.action)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                      {{ formatActionName(log.action) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate" :title="log.description">
                    {{ log.description || '—' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    <span v-if="log.entity_type" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                      {{ log.entity_type }} <span v-if="log.entity_id" class="ml-1 text-gray-500 dark:text-gray-400">#{{ log.entity_id }}</span>
                    </span>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-300">{{ log.ip_address || '—' }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ formatDate(log.created_at) }}</td>
                </tr>
                
                <!-- Empty State -->
                <tr v-if="filteredLogs.length === 0">
                  <td colspan="7" class="px-6 py-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No audit logs found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.total_pages > 1" class="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 gap-4">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              Showing 
              <span class="font-medium text-gray-900 dark:text-white">{{ (pagination.current_page - 1) * pagination.per_page + 1 }}</span> 
              to 
              <span class="font-medium text-gray-900 dark:text-white">{{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}</span> 
              of 
              <span class="font-medium text-gray-900 dark:text-white">{{ pagination.total }}</span> 
              entries
            </div>
          
            <div class="flex items-center gap-2">
              <button
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
            
              <div class="flex gap-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="changePage(page)"
                  :class="[
                    'w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors',
                    pagination.current_page === page 
                      ? 'bg-[#74bd54] text-white border border-[#74bd54]' 
                      : 'text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  ]"
                >
                  {{ page }}
                </button>
              </div>
            
              <button
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.total_pages"
                class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col justify-center items-center h-64">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-[#74bd54]"></div>
          <p class="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading audit logs...</p>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import HrSidebar from "@/components/hr/HrSidebar.vue";
import HrTopbar from "@/components/hr/HrTopbar.vue";
import { useToast } from 'vue-toastification';
import api from "@/services/api";   

const toast = useToast();

const currentPageTitle = ref("Audit Logs");
const isSidebarOpen = ref(false); // Hidden by default on mobile
const isDarkMode = ref(false);

const selectedAction = ref("");
const search = ref("");
const logs = ref([]);         
const fromDate = ref("");     
const toDate = ref("");
const showFilter = ref(false);

const pagination = ref({
  current_page: 1,
  per_page: 20,
  total: 0,
  total_pages: 1
});

const loading = ref(false);

// Fetch Logs
const fetchAuditLogs = async (page = 1) => {
  loading.value = true;
  try {
    const params = {
      page: page,
      limit: pagination.value.per_page,
      search: search.value,
      action: selectedAction.value,
      from_date: fromDate.value,
      to_date: toDate.value,
      // Removed hardcoded user_type array to let backend handle it or return all
    };

    const response = await api.get("AuditLogs.php", { params });

    logs.value = response.data.data || [];
    
    // Safe fallback for pagination in case API doesn't return it
    pagination.value = response.data.pagination || {
      current_page: 1,
      per_page: 20,
      total: logs.value.length,
      total_pages: 1
    };
  } catch (error) {
    console.error("Error fetching audit logs:", error);
    logs.value = [];
    toast.error("Failed to load audit logs");
  } finally {
    loading.value = false;
  }
};

// Watch filters and reset to page 1 when changed
watch([search, selectedAction, fromDate, toDate], () => {
  pagination.value.current_page = 1;
  fetchAuditLogs(1);
});

const changePage = (page) => {
  if (page < 1 || page > pagination.value.total_pages) return;
  pagination.value.current_page = page;
  fetchAuditLogs(page);
};

// ✅ FIXED: Removed the hardcoded user_type filter that was hiding "Admin" logs!
const filteredLogs = computed(() => {
  if (!Array.isArray(logs.value)) return [];
  
  // If there's no search query, return all logs
  if (!search.value) return logs.value;

  // Client-side search fallback
  return logs.value.filter(log =>
    Object.values(log).some(value =>
      value?.toString().toLowerCase().includes(search.value.toLowerCase())
    )
  );
});

const visiblePages = computed(() => {
  const pages = [];
  const total = pagination.value.total_pages || 1;
  const current = pagination.value.current_page;
  
  // Show a window of pages around the current page
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const resetFilters = () => {
  search.value = "";
  selectedAction.value = "";
  fromDate.value = "";
  toDate.value = "";
  // The watch will automatically trigger fetchAuditLogs(1)
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatActionName = (action) => {
  return action.replace(/_/g, ' ');
};

const getActionBadgeClass = (action) => {
  const a = action.toLowerCase();
  if (a.includes('success') || a.includes('view') || a.includes('create')) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  }
  if (a.includes('failed') || a.includes('delete') || a.includes('error')) {
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  }
  if (a.includes('update') || a.includes('edit')) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  }
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const exportLogs = () => {
  const dataToExport = filteredLogs.value.length > 0 ? filteredLogs.value : logs.value;
  
  if (dataToExport.length === 0) {
    toast.warning("No logs to export");
    return;
  }

  let csv = "ID,User,User Type,Action,Description,Entity Type,Entity ID,IP Address,Created At\n";
  
  dataToExport.forEach(log => {
    csv += `"${log.id}","${log.user_full_name || 'System'}","${log.user_type || 'Unknown'}","${log.action}","${(log.description || '').replace(/"/g, '""')}","${log.entity_type || ''}","${log.entity_id || ''}","${log.ip_address || ''}","${log.created_at || ''}"\n`;
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `audit-logs-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success("Logs exported successfully");
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark");
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove("dark");
    localStorage.theme = 'light';
  }
};

onMounted(() => {
  fetchAuditLogs();
});
</script>

<style scoped>
.filter-enter-active,
.filter-leave-active {
  transition: all 0.3s ease;
}
.filter-enter-from,
.filter-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}
.filter-enter-to,
.filter-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}
</style>